import type { ButtonMode, LauncherShape } from './enums.js';
import { NOTCH_SIZE_PX, resolveShape } from './shapes.js';

/**
 * How the edge tab draws an application's launcher shape (I22656, ADR 0036).
 *
 * The rule is that the shape decides the tab's ENDS. The tab is a vertical bar
 * docked to the right edge, so the button's silhouettes do not transfer as
 * they are: a 12px bottom-right chamfer would fall in the 9px of the tab that
 * sits off-screen, and a 1:1 shape cannot be 36 by 120. Shared here, not in
 * the widget, for the reason `resolveShape` is: the admin preview and the
 * widget must draw the same tab, and the portal cannot import the widget.
 */
export type EdgeTabVariant =
  /** Both ends slanted at the brand's 12 degrees: PARALLELOGRAM, and today's tab. */
  | 'SLANT'
  /** Square ends, and the configured radius on the two visible corners: RECTANGLE. */
  | 'SQUARE_ENDS'
  /** The top-left corner cut at 45 degrees, the one corner of the notch that is on screen. */
  | 'NOTCH'
  /** A 44px icon-only tile flush to the edge: SQUARE, TEARDROP and CUSTOM. */
  | 'COMPACT';

export interface EdgeTabShape {
  variant: EdgeTabVariant;
  /**
   * Inline style for the tab's visible shape: a corner radius, TEARDROP's own
   * radius, or CUSTOM's mask. Empty for SLANT and NOTCH, whose clip-paths live
   * in the stylesheet because they are written in the tab's own variables.
   */
  shapeStyle: Record<string, string>;
  showIcon: boolean;
  showLabel: boolean;
  /** Whether the configured radius shows anywhere, as `ShapeStyle.honoursRadius` says for the button. */
  honoursRadius: boolean;
}

/** The notch's cut, for the stylesheet and the preview: the button's chamfer size. */
export const EDGE_TAB_NOTCH_PX = NOTCH_SIZE_PX;

/**
 * Resolves an application's shape and display mode into the edge tab to draw.
 *
 * A shape that cannot carry a label (`resolveShape(...).iconOnly`) becomes the
 * compact tile and drops the label whatever the mode says, exactly as the
 * button drops it. Otherwise the mode decides: ICON shows the glyph alone,
 * TEXT the label alone, ICON_TEXT both.
 */
export function resolveEdgeTab(
  shape: LauncherShape | null | undefined,
  opts: { borderRadius: number; maskUrl?: string | null; buttonMode?: ButtonMode | null },
): EdgeTabShape {
  const mode = opts.buttonMode ?? 'ICON_TEXT';
  const byMode = { showIcon: mode !== 'TEXT', showLabel: mode !== 'ICON' };
  const radius = `${opts.borderRadius}px`;

  switch (shape ?? 'RECTANGLE') {
    case 'PARALLELOGRAM':
      return { variant: 'SLANT', shapeStyle: {}, ...byMode, honoursRadius: false };

    case 'NOTCH':
      return { variant: 'NOTCH', shapeStyle: {}, ...byMode, honoursRadius: false };

    case 'SQUARE':
    case 'TEARDROP':
    case 'CUSTOM': {
      const button = resolveShape(shape as LauncherShape, opts);
      // CUSTOM without a usable mask falls back to RECTANGLE in `resolveShape`;
      // follow it, so the tab and the button fall back together.
      if (!button.iconOnly) {
        return {
          variant: 'SQUARE_ENDS',
          shapeStyle: { borderTopLeftRadius: radius, borderBottomLeftRadius: radius },
          ...byMode,
          honoursRadius: true,
        };
      }
      return {
        variant: 'COMPACT',
        shapeStyle: button.backdropStyle,
        showIcon: true,
        showLabel: false,
        honoursRadius: button.honoursRadius,
      };
    }

    case 'RECTANGLE':
    default:
      // The right-hand corners sit in the bleed past the viewport edge, so
      // only the left two can show a radius.
      return {
        variant: 'SQUARE_ENDS',
        shapeStyle: { borderTopLeftRadius: radius, borderBottomLeftRadius: radius },
        ...byMode,
        honoursRadius: true,
      };
  }
}
